import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Undo2, Loader2, X } from "lucide-react";
import { toast } from "sonner";

import { useTranslation } from "react-i18next";
/**
 * UndoToast — floats bottom-center for `windowSec` seconds after a credit
 * entry is posted, letting the CSA reverse it with one tap. Fires
 * POST /credit-entries/{id}/void with no reason.
 *
 * Props:
 *   entry           { id, amount, customer_name } | null
 *   windowSec       number (from /credit-config)
 *   onUndone        () => void       — called after successful void
 *   onDismiss       () => void       — called when banner is manually dismissed / expires
 */
export default function UndoToast({ entry, windowSec = 60, onUndone, onDismiss }) {
  const { t } = useTranslation();
  const [remaining, setRemaining] = useState(windowSec);
  const [voiding, setVoiding] = useState(false);
  const startRef = useRef(null);

  useEffect(() => {
    if (!entry) return undefined;
    startRef.current = Date.now();
    setRemaining(windowSec);
    const tick = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startRef.current) / 1000);
      const left = Math.max(0, windowSec - elapsed);
      setRemaining(left);
      if (left <= 0) {
        clearInterval(tick);
        onDismiss?.();
      }
    }, 250);
    return () => clearInterval(tick);
  }, [entry, windowSec, onDismiss]);

  if (!entry) return null;

  const doUndo = async () => {
    setVoiding(true);
    try {
      await api.post(`/credit-entries/${entry.id}/void`, {});
      toast.success(t("components.undotoast.credit_entry_undone"));
      onUndone?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setVoiding(false); }
  };

  const pct = (remaining / windowSec) * 100;

  return (
    <div
      className="fixed inset-x-3 bottom-4 z-50 mx-auto max-w-sm rounded-2xl border border-emerald-300 bg-emerald-50 shadow-2xl md:inset-x-auto md:right-6"
      data-testid="undo-toast"
      role="alert"
      aria-live="polite"
    >
      {/* Countdown bar */}
      <div className="h-1 overflow-hidden rounded-t-2xl bg-emerald-200">
        <div
          className="h-full bg-emerald-600 transition-all duration-200 ease-linear"
          style={{ width: `${pct}%` }}
          data-testid="undo-toast-progress"
        />
      </div>

      <div className="flex items-start gap-3 p-3">
        <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-full bg-emerald-600 text-white">
          ✓
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold text-emerald-950">
            Credit entry posted
          </div>
          <div className="mt-0.5 truncate text-[11px] text-emerald-900/80">
            ₹{Number(entry.amount || 0).toLocaleString("en-IN")}{entry.customer_name ? ` · ${entry.customer_name}` : ""}
          </div>
          <div className="mt-2 flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              className="h-8 border-emerald-500 bg-white text-emerald-800 hover:bg-emerald-100"
              onClick={doUndo}
              disabled={voiding || remaining <= 0}
              data-testid="undo-toast-btn"
            >
              {voiding ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Undo2 className="mr-1 h-3.5 w-3.5" />}
              Undo ({remaining}s)
            </Button>
          </div>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="rounded-full p-1 text-emerald-800/60 hover:bg-emerald-100 hover:text-emerald-900"
          data-testid="undo-toast-dismiss"
          aria-label={t("components.undotoast.dismiss")}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
